import { debounce } from 'lodash';
import { useId, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Checkbox, ContextualMenu, EmptyState, InputSearch, TooltipButton } from 'sonata-react-components';
import { formatNumber, formatPercentage } from 'sonata-static-storage/lib/helpers/generalFormatters.helpers';

import { formatMoney } from '@/components/driveToStore/common/helpers/formatters';
import { getDriveToStoreReportUrl } from '@/components/driveToStore/common/hooks/useDriveToStoreNavigation';
import { isSharedReportSession } from '@/helpers/shareReport/shareReport.helper';
import { useFootfallReportSocket } from '@/hooks/useFootfallReportSocket';
import { getLineItemComparisonFile } from '@/services/footfall.service';
import { useGlobalStore } from '@/store/global';
import { getBaseCurrency } from '@/store/global/baseCurrency/selectors';
import { getSession } from '@/store/global/session/selectors';

import type { IntlShape } from 'react-intl';
import type { BaseCurrencyInterface } from 'sonata-static-storage/lib/schemas/baseCurrency/baseCurrency.schema';
import type { LineItemComparisonFileFormat, LineItemComparisonRow } from '@/services/footfall.service';

import './lineItemComparisonTable.scss';

type Props = {
  entityType: string;
  entityId: string;
  country?: string;
  userId: string;
  height?: number;
  items: LineItemComparisonRow[];
  isError: boolean;
  onRetry: () => void;
  sharedReportFullAccess?: boolean;
};

type State = {
  nameFilter: string;
  perPoiMode: boolean;
  generatingFormat: LineItemComparisonFileFormat | null;
};

const BROADCAST_ENTITY_ID_SUFFIX = '::lineItemComparison';

function formatUpliftPct(value: number | null, country: string): string {
  if (value == null) {
    return '—';
  }

  const formatted = formatPercentage(value, { base: 100, country, fixedDecimals: 2 });
  return value > 0 ? `+${formatted}` : formatted;
}

function formatRoi(value: number | null, country: string): string {
  if (value == null) {
    return '—';
  }
  return formatPercentage(value, { base: 100, country, fixedDecimals: 2 });
}

function formatInvestment(
  investment: number | null,
  poiCount: number,
  perPoiMode: boolean,
  baseCurrency: BaseCurrencyInterface,
  formatMessage: IntlShape['formatMessage']
): string {
  if (!perPoiMode) {
    return formatMoney(investment, baseCurrency);
  }
  if (investment == null || poiCount <= 0) {
    return '—';
  }
  return formatMessage(
    {
      defaultMessage: '{value} /POI',
      id: 'driveToStore.aggregatedReport.lineItemComparison.investmentPerPoi'
    },
    { value: formatMoney(investment / poiCount, baseCurrency) }
  );
}

function formatUpliftSubMetric(
  uplift: number | null,
  poiCount: number,
  perPoiMode: boolean,
  country: string,
  formatMessage: IntlShape['formatMessage']
): string {
  if (uplift == null) {
    return '—';
  }
  const value = perPoiMode ? (poiCount > 0 ? uplift / poiCount : null) : uplift;
  if (value == null) {
    return '—';
  }
  const formatted = formatNumber(value, { country, fixed: 0 });
  const withSign = value > 0 ? `+${formatted}` : formatted;
  return perPoiMode
    ? formatMessage(
        {
          defaultMessage: '{value} /POI',
          id: 'driveToStore.aggregatedReport.lineItemComparison.upliftPerPoi'
        },
        { value: withSign }
      )
    : formatMessage(
        {
          defaultMessage: '{value} visits',
          id: 'driveToStore.aggregatedReport.lineItemComparison.upliftVisits'
        },
        { value: withSign }
      );
}

export default function LineItemComparisonTable({
  entityType,
  entityId,
  country,
  userId,
  height = 320,
  items,
  isError,
  onRetry,
  sharedReportFullAccess
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const isShared = isSharedReportSession(useGlobalStore(getSession));
  const canOpenSingleReport = !isShared || sharedReportFullAccess;
  const baseCurrency = useGlobalStore(getBaseCurrency);
  const formatCountry = baseCurrency?.countries?.[0] || 'default';
  const perPoiModeCheckboxId = useId();

  const [{ nameFilter, perPoiMode, generatingFormat }, setState] = useState<State>({
    generatingFormat: null,
    nameFilter: '',
    perPoiMode: false
  });

  const term = nameFilter.trim().toLowerCase();
  const filteredItems = term ? items.filter(item => item.name.toLowerCase().includes(term)) : items;

  const handleSearchChange = useMemo(
    () =>
      debounce((value: string) => {
        setState(prevState => ({ ...prevState, nameFilter: value.trim() }));
      }, 400),
    []
  );

  const handleFileReady = (urlFile: string, fileFormat: string) => {
    setState(prevState => ({ ...prevState, generatingFormat: null }));
    const extension = fileFormat === 'csv' ? 'csv' : 'xlsx';
    fetch(urlFile)
      .then(res => res.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `line_item_comparison.${extension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => {
        window.open(urlFile, '_blank', 'noopener,noreferrer');
      });
  };

  const handleFileError = () => {
    setState(prevState => ({ ...prevState, generatingFormat: null }));
  };

  useFootfallReportSocket({
    entityId: `${entityId}${BROADCAST_ENTITY_ID_SUFFIX}`,
    onFileError: handleFileError,
    onFileReady: handleFileReady
  });

  const handleDownload = async (fileFormat: LineItemComparisonFileFormat) => {
    if (generatingFormat !== null) {
      return;
    }
    setState(prevState => ({ ...prevState, generatingFormat: fileFormat }));
    try {
      await getLineItemComparisonFile(entityType, entityId, userId, { country, fileFormat, perPoi: perPoiMode });
    } catch {
      setState(prevState => ({ ...prevState, generatingFormat: null }));
    }
  };

  const handleOpenReport = (lineItemId: string) => {
    const url = getDriveToStoreReportUrl(entityType, lineItemId);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="lineItemComparisonTable">
      <div className="lineItemComparisonTable__header">
        <span className="lineItemComparisonTable__icon fa-duotone fa-solid fa-list" />
        <h3 className="lineItemComparisonTable__title">
          {formatMessage({
            defaultMessage: 'Line item comparison',
            id: 'driveToStore.aggregatedReport.lineItemComparison.title'
          })}
        </h3>
        <div className="lineItemComparisonTable__headerRight">
          <Checkbox
            active={perPoiMode}
            id={perPoiModeCheckboxId}
            labelText={formatMessage({
              defaultMessage: 'View information per POI',
              id: 'driveToStore.aggregatedReport.lineItemComparison.perPoiMode'
            })}
            onClickAction={() => setState(prevState => ({ ...prevState, perPoiMode: !prevState.perPoiMode }))}
            type="checkbox"
          />
          {canOpenSingleReport && (
            <ContextualMenu
              buttonConfig={{
                icon: 'fa-regular fa-arrow-down-to-line',
                loading: generatingFormat !== null,
                shape: 'squareRounded',
                size: 'ultraSmall',
                text: formatMessage({
                  defaultMessage: 'Download list',
                  id: 'driveToStore.aggregatedReport.lineItemComparison.downloadList'
                }),
                type: 'secondary'
              }}
              id={'lineItemComparisonTable-download'}
            >
              <ContextualMenu.Item
                icon="far fa-file-csv"
                id={'lineItemComparisonTable-download-csv'}
                loading={generatingFormat === 'csv'}
                onClick={() => handleDownload('csv')}
                text={formatMessage({
                  defaultMessage: 'Download .CSV',
                  id: 'driveToStore.aggregatedReport.lineItemComparison.downloadCsv'
                })}
              />
              <ContextualMenu.Item
                icon="far fa-file-excel"
                id={'lineItemComparisonTable-download-excel'}
                loading={generatingFormat === 'excel'}
                onClick={() => handleDownload('excel')}
                text={formatMessage({
                  defaultMessage: 'Download Excel',
                  id: 'driveToStore.aggregatedReport.lineItemComparison.downloadExcel'
                })}
              />
            </ContextualMenu>
          )}
          <div className="lineItemComparisonTable__search">
            <InputSearch
              id={'lineItemComparisonTable__search'}
              lens
              name="lineItemComparisonTableSearch"
              onChange={handleSearchChange}
              placeholder={formatMessage({
                defaultMessage: 'Search by name',
                id: 'driveToStore.aggregatedReport.lineItemComparison.searchPlaceholder'
              })}
              tiny
            />
          </div>
        </div>
      </div>
      <div className="lineItemComparisonTable__listColumnsBar">
        <span className="lineItemComparisonTable__colLineItem">
          {formatMessage({
            defaultMessage: 'LINE ITEM',
            id: 'driveToStore.aggregatedReport.lineItemComparison.column.lineItem'
          })}
        </span>
        <span className="lineItemComparisonTable__colPois">
          {formatMessage({
            defaultMessage: 'POIS',
            id: 'driveToStore.aggregatedReport.lineItemComparison.column.pois'
          })}
        </span>
        <span className="lineItemComparisonTable__colInvestment">
          {formatMessage({
            defaultMessage: 'INVESTMENT',
            id: 'driveToStore.aggregatedReport.lineItemComparison.column.investment'
          })}
        </span>
        <span className="lineItemComparisonTable__colUplift">
          {formatMessage({
            defaultMessage: 'UPLIFT',
            id: 'driveToStore.aggregatedReport.lineItemComparison.column.uplift'
          })}
        </span>
        <span className="lineItemComparisonTable__colCpsv">
          {formatMessage({
            defaultMessage: 'CPSV',
            id: 'driveToStore.aggregatedReport.lineItemComparison.column.cpsv'
          })}
        </span>
        <span className="lineItemComparisonTable__colRoi">
          {formatMessage({ defaultMessage: 'ROI', id: 'driveToStore.aggregatedReport.lineItemComparison.column.roi' })}
        </span>
        <span className="lineItemComparisonTable__colNav" />
      </div>
      <div className="lineItemComparisonTable__body" style={{ maxHeight: height }}>
        {isError ? (
          <EmptyState
            buttonProps={{
              onClick: onRetry,
              text: formatMessage({
                defaultMessage: 'Retry',
                id: 'driveToStore.aggregatedReport.lineItemComparison.retry'
              })
            }}
            description={formatMessage({
              defaultMessage: 'Please try again.',
              id: 'driveToStore.aggregatedReport.lineItemComparison.errorDescription'
            })}
            icon="fal fa-triangle-exclamation"
            show
            title={formatMessage({
              defaultMessage: 'Something went wrong loading this data',
              id: 'driveToStore.aggregatedReport.lineItemComparison.errorTitle'
            })}
          />
        ) : filteredItems.length === 0 ? (
          <EmptyState
            description={
              term
                ? formatMessage({
                    defaultMessage: 'Try a different search term.',
                    id: 'driveToStore.aggregatedReport.lineItemComparison.noSearchResultsDescription'
                  })
                : undefined
            }
            show
            title={
              term
                ? formatMessage({
                    defaultMessage: 'No line items match your search',
                    id: 'driveToStore.aggregatedReport.lineItemComparison.noSearchResultsTitle'
                  })
                : formatMessage({
                    defaultMessage: 'There is no data for the current selection.',
                    id: 'driveToStore.aggregatedReport.lineItemComparison.emptyState'
                  })
            }
          />
        ) : (
          filteredItems.map(item => (
            <div className="lineItemComparisonTable__row" key={item.id}>
              <div className="lineItemComparisonTable__lineItemCell">
                <span className="lineItemComparisonTable__lineItemName" title={item.name}>
                  {item.name}
                </span>
                {item.campaignPlanName && (
                  <span className="lineItemComparisonTable__lineItemMeta">
                    {formatMessage(
                      {
                        defaultMessage: 'Campaign plan: {name}',
                        id: 'driveToStore.aggregatedReport.lineItemComparison.campaignPlan'
                      },
                      { name: item.campaignPlanName }
                    )}
                  </span>
                )}
                {item.ioName && (
                  <span className="lineItemComparisonTable__lineItemMeta">
                    {formatMessage(
                      {
                        defaultMessage: 'Insertion Order: {name}',
                        id: 'driveToStore.aggregatedReport.lineItemComparison.io'
                      },
                      { name: item.ioName }
                    )}
                  </span>
                )}
              </div>
              <span className="lineItemComparisonTable__colPois lineItemComparisonTable__pois">
                {formatNumber(item.poiCount, { country: formatCountry, fixed: 0 })}
              </span>
              <span className="lineItemComparisonTable__colInvestment lineItemComparisonTable__investment">
                {formatInvestment(item.investment, item.poiCount, perPoiMode, baseCurrency, formatMessage)}
              </span>
              <div className="lineItemComparisonTable__upliftCell">
                <span className="lineItemComparisonTable__upliftPct">
                  {formatUpliftPct(item.upliftPct, formatCountry)}
                </span>
                <span className="lineItemComparisonTable__upliftSub">
                  {formatUpliftSubMetric(item.uplift, item.poiCount, perPoiMode, formatCountry, formatMessage)}
                </span>
              </div>
              <span className="lineItemComparisonTable__colCpsv lineItemComparisonTable__cpsv">
                {formatMoney(item.costPerVisit, baseCurrency)}
              </span>
              <span className="lineItemComparisonTable__colRoi lineItemComparisonTable__roi">
                {formatRoi(item.roi, formatCountry)}
              </span>
              <span className="lineItemComparisonTable__colNav">
                {canOpenSingleReport && (
                  <TooltipButton
                    buttonConfig={{
                      buttonOnClick: () => handleOpenReport(item.id),
                      buttonShape: 'link',
                      buttonSize: 'small'
                    }}
                    icon="fa-solid fa-grid"
                    iconColors={{
                      hoverIconBackgroundColor: '',
                      hoverIconColor: '',
                      iconColor: 'var(--color-sonata-interaction-500)'
                    }}
                    id={`lineItemComparisonTable-openReport-${item.id}`}
                    isButton
                    tooltipConfig={{ tooltipEvent: 'mouseenter', tooltipPlace: 'top' }}
                  >
                    {formatMessage({
                      defaultMessage: 'Open Line Item report',
                      id: 'driveToStore.aggregatedReport.lineItemComparison.openReport'
                    })}
                  </TooltipButton>
                )}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
